import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "layouts/authentication/AuthContext";
import { getAllLeads } from "layouts/tables/api";
import MDTypography from "components/MDTypography";
import "./search.css";

const GlobalSearch = ({ open, onClose }) => {
  const { tenantId, userId, userRole, rmId } = useAuth();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  // Fetch all leads once when dialog opens
  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }

    let cancelled = false;
    const fetchLeads = async () => {
      setLoading(true);
      setError(false);
      setLeads([]);
      try {
        const res = await getAllLeads(tenantId, userId, userRole, rmId, null);
        const records = res?.data || res;
        if (res?.success === false || !Array.isArray(records)) {
          throw new Error("Unable to load search results");
        }
        if (!cancelled) setLeads(records);
      } catch (err) {
        console.error("GlobalSearch fetch error:", err);
        if (!cancelled) {
          setLeads([]);
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLeads();

    // Auto-focus the input after dialog opens
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => {
      cancelled = true;
      clearTimeout(focusTimer);
    };
  }, [open, tenantId, userId, userRole, rmId]);

  // Recalculate after data arrives as well as while typing, including numeric phone values.
  const filtered = useMemo(() => {
    const lower = query.trim().toLowerCase();
    if (!open || !lower) return [];
    return leads
      .filter((lead) =>
        [
          lead.lead_name,
          lead.company_name,
          lead.phone_number,
          lead.email,
          lead.status_name,
          lead.source_name,
        ].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(lower)
        )
      )
      .slice(0, 10);
  }, [leads, query, open]);

  const handleSelect = (lead) => {
    onClose();
    navigate(`/leads/${lead.id}`);
  };

  const showEmpty =
    !loading && !error && query.trim().length > 0 && filtered.length === 0;
  const showResults = !loading && filtered.length > 0;

  return (
    <Dialog
      id="global-search-dialog"
      className="crm-global-search"
      aria-label="Search CRM"
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(15, 23, 42, 0.35)",
          backdropFilter: "blur(3px)",
        },
      }}
      PaperProps={{
        "aria-label": "Search CRM",
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          border: "1px solid #E4E7EC",
          boxShadow: "0 24px 80px rgba(15, 23, 42, 0.24)",
        },
      }}
      transitionDuration={150}
    >
      <Box
        px={{ xs: 2, sm: 3 }}
        pt={2}
        pb={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{ flexShrink: 0 }}
      >
        <MDTypography variant="h6" sx={{ color: "#0F172A", fontWeight: 800 }}>
          Global search
        </MDTypography>
        <IconButton
          aria-label="Close search"
          onClick={onClose}
          sx={{ width: 44, height: 44, color: "#64748B", borderRadius: 2 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0, overscrollBehavior: "contain" }}>
        <Box px={{ xs: 2, sm: 3 }} pb={2}>
          <TextField
            inputRef={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" &&
                !event.nativeEvent.isComposing &&
                !loading &&
                filtered.length
              ) {
                event.preventDefault();
                handleSelect(filtered[0]);
              }
            }}
            placeholder="Search leads, companies, phone or email"
            inputProps={{ "aria-label": "Search leads" }}
            fullWidth
            variant="outlined"
            size="small"
            autoComplete="off"
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 52,
                borderRadius: "12px",
                backgroundColor: "#F8FAFC",
                "&.Mui-focused": {
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0 0 0 3px rgba(79, 70, 229, 0.12)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4F46E5",
                  borderWidth: 1,
                },
              },
              "& input": { fontSize: 16, py: 1.5 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {showResults && (
          <>
            <Divider />
            <Box
              px={{ xs: 2, sm: 3 }}
              py={1}
              display="flex"
              justifyContent="space-between"
              gap={1}
            >
              <MDTypography
                variant="caption"
                sx={{ color: "#64748B", fontWeight: 700 }}
              >
                LEADS
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{ color: "#64748B" }}
                aria-live="polite"
              >
                Showing {filtered.length}
                {filtered.length === 10 ? " (up to 10)" : ""}
              </MDTypography>
            </Box>
            <List disablePadding sx={{ px: 1, pb: 1 }}>
              {filtered.map((lead, index) => (
                <React.Fragment key={lead.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      component={RouterLink}
                      to={`/leads/${lead.id}`}
                      onClick={(event) => {
                        if (!event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) onClose();
                      }}
                      sx={{
                        minWidth: 0,
                        overflowWrap: "anywhere",
                        px: { xs: 1, sm: 2 },
                        py: 1.5,
                        borderRadius: "12px",
                        cursor: "pointer",
                        "&:hover, &.Mui-focusVisible": {
                          backgroundColor: "#EEF2FF",
                        },
                      }}
                    >
                      <ListItemText
                        sx={{ m: 0, minWidth: 0 }}
                        secondaryTypographyProps={{ component: "div" }}
                        primary={
                          <MDTypography
                            variant="button"
                            sx={{
                              color: "#0F172A",
                              fontSize: 14,
                              fontWeight: 700,
                            }}
                          >
                            {lead.lead_name || "Untitled lead"}
                          </MDTypography>
                        }
                        secondary={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={0.5}
                            flexWrap="wrap"
                          >
                            {lead.company_name && (
                              <MDTypography variant="caption" color="text">
                                {lead.company_name}
                              </MDTypography>
                            )}
                            {lead.phone_number && (
                              <MDTypography variant="caption" color="text">
                                · {lead.phone_number}
                              </MDTypography>
                            )}
                            {lead.status_name && (
                              <Chip
                                label={lead.status_name}
                                size="small"
                                sx={{
                                  maxWidth: "100%",
                                  height: 24,
                                  fontSize: 11,
                                  backgroundColor: "#F1F5F9",
                                  color: "#334155",
                                  border: "1px solid #E4E7EC",
                                }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < filtered.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          </>
        )}

        {showEmpty && (
          <Box
            px={3}
            py={4}
            textAlign="center"
            sx={{ overflowWrap: "anywhere" }}
          >
            <SearchIcon sx={{ color: "#94A3B8", fontSize: 28, mb: 1 }} />
            <MDTypography
              variant="body2"
              sx={{ color: "#334155", fontWeight: 600 }}
            >
              No leads found for &ldquo;{query}&rdquo;
            </MDTypography>
            <MDTypography
              variant="caption"
              sx={{ color: "#64748B", display: "block", mt: 1 }}
            >
              Try another name, company, phone number or email.
            </MDTypography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            Could not load leads. Close search and try again.
          </Alert>
        )}

        {!loading && !error && !query.trim() && (
          <Box px={3} py={4} textAlign="center">
            <Box
              sx={{
                display: "inline-flex",
                p: 1.5,
                mb: 1.5,
                borderRadius: "16px",
                backgroundColor: "#EEF2FF",
                color: "#4F46E5",
              }}
            >
              <SearchIcon />
            </Box>
            <MDTypography variant="h6" sx={{ color: "#0F172A" }}>
              Find a lead, fast
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{ color: "#64748B", mt: 0.5, fontSize: 14 }}
            >
              Search by name, company, phone or email.
            </MDTypography>
          </Box>
        )}
        {loading && (
          <Box px={3} py={4} textAlign="center" role="status">
            <MDTypography variant="body2" sx={{ color: "#64748B" }}>
              Loading your leads…
            </MDTypography>
          </Box>
        )}
      </DialogContent>
      <Box
        px={{ xs: 2, sm: 3 }}
        py={1.5}
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        gap={1}
        sx={{
          flexShrink: 0,
          borderTop: "1px solid #E4E7EC",
          backgroundColor: "#F8FAFC",
        }}
      >
        <MDTypography variant="caption" sx={{ color: "#64748B" }}>
          Enter to open first result
        </MDTypography>
        <MDTypography variant="caption" sx={{ color: "#64748B" }}>
          Esc to close
        </MDTypography>
      </Box>
    </Dialog>
  );
};

export default GlobalSearch;
